package com.evoapps.evoshift.domain.usecase

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.domain.model.AvailabilityEmailLinks
import com.evoapps.evoshift.domain.model.AvailabilityPeriod
import com.evoapps.evoshift.domain.model.AvailabilityRequest
import com.evoapps.evoshift.domain.repository.AvailabilityRepository
import com.evoapps.evoshift.domain.repository.RosterRepository
import com.evoapps.evoshift.domain.model.Notification
import com.evoapps.evoshift.domain.model.NotificationData
import com.evoapps.evoshift.domain.model.NotificationType
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.capacity.monthRange
import com.evoapps.evoshift.domain.schedule.ScheduleRange
import kotlinx.datetime.LocalDate
import kotlin.random.Random
import kotlin.time.Instant

/**
 * A manager asks the team for availability for a roster's month: the request is stored and linked
 * to the roster, and every active member of the workplace is notified (inbox and push). The
 * period may be part of the month (e.g. days 1–15, SH-48) but never leaves it.
 */
class SendAvailabilityRequestUseCase(
    private val availabilityRepository: AvailabilityRepository,
    private val rosterRepository: RosterRepository,
    private val userRepository: UserRepository? = null,
    private val now: () -> Instant = { AppClock.now() }
) {
    suspend operator fun invoke(
        managerId: String,
        workplaceId: String,
        rosterId: String,
        month: String,
        startDate: String,
        endDate: String,
        dueDate: String,
        message: String?,
        /** SH-173: also by e-mail with a personal link to the web form (sent by the functions). */
        emailLinks: AvailabilityEmailLinks = AvailabilityEmailLinks.NONE
    ): Result<Unit> {
        return try {
            requirePeriodInMonth(month, startDate, endDate)
            val requestId = "av_req_${Random.nextInt(100000, 999999)}"
            val now = now().toString()

            val request = AvailabilityRequest(
                id = requestId,
                workplaceId = workplaceId,
                rosterId = rosterId,
                month = month,
                period = AvailabilityPeriod(startDate, endDate),
                dueDate = dueDate,
                sentAt = now,
                createdBy = managerId,
                message = message,
                emailLinks = emailLinks
            )

            // 1. Create the request
            availabilityRepository.createAvailabilityRequest(request).getOrThrow()

            // 2. Link to roster
            rosterRepository.linkAvailabilityRequest(rosterId, requestId).getOrThrow()

            // 3. Tell the team (best effort: the request exists even if a notification fails)
            notifyTeam(managerId, workplaceId, rosterId, month, startDate, endDate, dueDate, message, now)

            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    private suspend fun notifyTeam(
        managerId: String, workplaceId: String, rosterId: String, month: String,
        startDate: String, endDate: String, dueDate: String, message: String?, now: String
    ) {
        val users = userRepository ?: return
        val (year, monthNumber) = month.split("-").let { (it.getOrNull(0) ?: "") to (it.getOrNull(1)?.toIntOrNull()?.toString() ?: "") }
        users.getAssignableEmployees(workplaceId).getOrNull().orEmpty()
            .filter { it.uid != managerId && it.engagements[workplaceId]?.isActive == true }
            .forEach { employee ->
                users.sendNotification(
                    Notification(
                        userId = employee.uid,
                        title = "Availability requested",
                        message = message?.takeIf { it.isNotBlank() } ?: "Please submit your availability for $month by ${dueDate.take(10)}.",
                        type = NotificationType.AVAILABILITY_REQUEST,
                        createdAt = now,
                        data = buildMap {
                            put(NotificationData.ROSTER_ID, rosterId)
                            put(NotificationData.WORKPLACE_ID, workplaceId)
                            put(NotificationData.MONTH, monthNumber)
                            put(NotificationData.YEAR, year)
                            put(NotificationData.DATE, dueDate.take(10))
                            // Part of the month: the days asked for (the app shows "days 1–15").
                            if (!isWholeMonth(month, startDate, endDate)) {
                                put(NotificationData.START, startDate.take(10))
                                put(NotificationData.END, endDate.take(10))
                            }
                            message?.takeIf { it.isNotBlank() }?.let { put(NotificationData.NOTE, it) }
                        }
                    )
                )
            }
    }
}

/** "YYYY-MM" as the month's range; null when it is not a month. */
private fun monthOf(month: String): ScheduleRange? = runCatching {
    val (year, number) = month.split("-").map { it.toInt() }
    monthRange(year, number)
}.getOrNull()

private fun isWholeMonth(month: String, startDate: String, endDate: String): Boolean =
    monthOf(month)?.let { it.start.toString() == startDate.take(10) && it.endInclusive.toString() == endDate.take(10) } ?: true

/** The period is a range of days of [month] ("YYYY-MM"): start on or before the end, both inside it. */
internal fun requirePeriodInMonth(month: String, startDate: String, endDate: String) {
    val range = monthOf(month) ?: return
    val start = LocalDate.parse(startDate.take(10))
    val end = LocalDate.parse(endDate.take(10))
    require(start <= end) { "The period ends before it starts" }
    require(start >= range.start && end <= range.endInclusive) { "The period must be within $month" }
}
